// Market Insights content hub. Articles are authored here so the /insights
// index and article pages share one source of truth for metadata, TOC sections
// and schema generation.

import imgIronVolatility from "@/assets/blog/iron-price-volatility-2026.jpg";
import imgHms1 from "@/assets/blog/hms1-yard.jpg";
import imgSpongeIron from "@/assets/blog/sponge-iron.jpg";
import imgGstDigital from "@/assets/blog/gst-digital-invoicing.jpg";
import imgCircular from "@/assets/blog/circular-economy-india.jpg";
import imgEmergingHubs from "@/assets/blog/emerging-foundry-hubs.jpg";
import imgAiQuality from "@/assets/blog/ai-quality-disputes.jpg";
import imgEscrowCompliance from "@/assets/blog/escrow-compliance.jpg";

export const INSIGHT_CATEGORIES = [
  "Market Trends",
  "Scrap Quality & Grading",
  "Policy & GST",
  "Sustainable Steel",
] as const;

export type InsightCategory = (typeof INSIGHT_CATEGORIES)[number];

export interface InsightSection {
  id: string;
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface InsightArticle {
  slug: string;
  title: string;
  excerpt: string;
  category: InsightCategory;
  author: string;
  authorRole: string;
  date: string;
  isoDate: string;
  readTime: string;
  image: string;
  /** Material slug used by the related-lots widget */
  relatedMaterial?: string;
  /** City slug used by the related-lots widget */
  relatedCity?: string;
  sections: InsightSection[];
}

export const INSIGHTS: InsightArticle[] = [
  {
    slug: "hms-scrap-price-outlook-india-2026",
    title: "HMS Scrap Price Outlook: What Is Moving Indian Rates in 2026",
    excerpt:
      "Infrastructure demand, imported offer levels and monsoon-hit collection are pulling HMS 1 in three directions at once. Here is how to read the daily print.",
    category: "Market Trends",
    author: "LohaLink Market Desk",
    authorRole: "Price benchmarking team",
    date: "August 1, 2026",
    isoDate: "2026-08-01",
    readTime: "7 min read",
    image: imgIronVolatility,
    relatedMaterial: "hms-1",
    relatedCity: "durgapur",
    sections: [
      {
        id: "demand-side",
        heading: "Demand: long products still lead",
        paragraphs: [
          "Rebar and structural demand from national highway, metro and housing projects continues to absorb most of the secondary sector's output. Induction furnace utilisation across Mandi Gobindgarh, Durgapur and Raipur has stayed in the high-70s through the year, which keeps a firm floor under HMS 1 and sponge iron.",
          "The practical implication for yards is that discounts are shallow even in weak weeks: buyers defer volume rather than push rates down, so a patient seller usually recovers the spread within a fortnight.",
        ],
      },
      {
        id: "supply-side",
        heading: "Supply: collection is the real variable",
        paragraphs: [
          "Domestic generation is dominated by demolition, railway releases and machining scrap. All three are seasonal. Monsoon weeks slow lifting and transport in eastern and central India, tightening ex-yard availability precisely when furnace demand is steady.",
          "Imported offers cap the upside. When landed shredded and HMS from the Gulf and West Africa becomes competitive at coastal clusters, Mumbai, Chennai and Ahmedabad prices decouple from inland hubs by 1–3%.",
        ],
      },
      {
        id: "how-to-read-the-print",
        heading: "How to read a daily benchmark responsibly",
        paragraphs: [
          "A single number is never the whole market. Treat a published rate as the mid-point of an ex-yard band and adjust for three things before you quote.",
        ],
        bullets: [
          "Grade reality: actual thickness mix and contamination move realised price by ₹800–₹1,500 per tonne",
          "Freight: inland movement of 200–400 km typically costs ₹700–₹1,200 per tonne",
          "Payment terms: cash-against-delivery, escrow and 30-day credit are not the same price",
        ],
      },
      {
        id: "playbook",
        heading: "A simple playbook for both sides",
        paragraphs: [
          "Sellers should list tonnage with photographic evidence and a declared grade band, then let verified buyers compete rather than negotiating serially with brokers. Buyers should lock the rate in escrow at booking and settle on verified net weight at destination, which removes the two largest sources of dispute from the transaction.",
        ],
      },
    ],
  },
  {
    slug: "grading-hms-1-vs-hms-2-field-guide",
    title: "Grading in the Field: Telling HMS 1 from HMS 2 Before You Pay",
    excerpt:
      "A practical inspection checklist for buyers and yard supervisors — thickness, contamination, bulk density and the four rejections worth walking away from.",
    category: "Scrap Quality & Grading",
    author: "Kaustav Deb",
    authorRole: "Founder, LohaLink",
    date: "July 24, 2026",
    isoDate: "2026-07-24",
    readTime: "6 min read",
    image: imgHms1,
    relatedMaterial: "hms-1",
    sections: [
      {
        id: "thickness",
        heading: "Start with thickness, not appearance",
        paragraphs: [
          "HMS 1 is defined by a 6mm minimum thickness and the absence of coated, sealed or cast pieces. HMS 2 permits 3mm and above and allows galvanised sheet. A yard walk with a simple gauge settles most grade arguments in ten minutes.",
        ],
      },
      {
        id: "contamination",
        heading: "The four rejections worth walking away from",
        paragraphs: [
          "Certain contaminants cost far more than the tonnage is worth, either through yield loss or safety risk.",
        ],
        bullets: [
          "Sealed containers and closed pipes — explosion risk in the furnace",
          "Oil-soaked turnings and coolant sludge — yield loss and emissions",
          "Non-ferrous attachments and bearings — chemistry contamination",
          "Concrete-embedded structural pieces — dead weight billed as steel",
        ],
      },
      {
        id: "density",
        heading: "Bulk density decides your real cost per tonne",
        paragraphs: [
          "Two lots at the same rate can differ by 15% in delivered value if one is loose sheet and the other is prepared heavy melt. Ask for the loaded volume alongside net weight, and price light material at the discount it deserves.",
        ],
      },
      {
        id: "documentation",
        heading: "Document the grade before the truck leaves",
        paragraphs: [
          "Geo-tagged loading photographs, a declared grade band and a weighbridge slip captured at dispatch turn a subjective argument into a factual one. On LohaLink each lot carries this evidence, and AI vision analysis flags visible contamination before the buyer commits funds.",
        ],
      },
    ],
  },
  {
    slug: "gst-on-scrap-metal-india-explained",
    title: "GST on Scrap Metal in India: Rates, RCM and the Paperwork That Matters",
    excerpt:
      "Ferrous scrap sits at 18% under HSN 7204, dolomite at 5%. What changes for unregistered purchases, e-way bills and input credit — explained without jargon.",
    category: "Policy & GST",
    author: "LohaLink Compliance Desk",
    authorRole: "Trade compliance",
    date: "July 15, 2026",
    isoDate: "2026-07-15",
    readTime: "8 min read",
    image: imgGstDigital,
    relatedMaterial: "hms-2",
    sections: [
      {
        id: "rates",
        heading: "Where the common grades sit",
        paragraphs: [
          "Ferrous scrap under HSN 7204 — HMS 1, HMS 2, turnings, shredded and cast iron scrap — attracts 18% GST. Semi-finished and finished steel such as billets (7207) and TMT bars (7214) are also at 18%. Dolomite (2518) is taxed at 5%. Rates are indicative of the current schedule and should be confirmed with your tax advisor for each dispatch.",
        ],
      },
      {
        id: "urp",
        heading: "Unregistered purchases (URP)",
        paragraphs: [
          "A large share of collection-level supply comes from unregistered vendors. Buying from a URP source shifts the compliance burden onto the buyer and complicates input credit. The clean alternative is to transact with GST-verified counterparties whose GSTIN status is checked at onboarding — which is why every seller on LohaLink is validated before listing.",
        ],
      },
      {
        id: "documents",
        heading: "The documents an audit will ask for",
        paragraphs: [
          "Keep the following for every consignment, digitally and in one place.",
        ],
        bullets: [
          "Tax invoice with correct HSN and place of supply",
          "E-way bill for consignments above the state threshold",
          "Weighbridge slips at dispatch and delivery",
          "Transporter details, vehicle number and delivery acknowledgement",
        ],
      },
      {
        id: "credit",
        heading: "Protecting input tax credit",
        paragraphs: [
          "Credit is denied most often for mundane reasons: a mismatched GSTIN, an invoice raised in the wrong state, or a supplier who never filed. Verifying the counterparty before dispatch and generating the invoice from platform-held trade data eliminates almost all of these failures.",
        ],
      },
    ],
  },
  {
    slug: "sponge-iron-procurement-2026",
    title: "Sponge Iron Procurement: Metallisation, Fines and Landed Cost",
    excerpt:
      "Why an 82% metallisation lot at a lower rate can cost more than an 86% lot, and how to structure DRI contracts around yield rather than headline price.",
    category: "Market Trends",
    author: "LohaLink Market Desk",
    authorRole: "Price benchmarking team",
    date: "July 3, 2026",
    isoDate: "2026-07-03",
    readTime: "6 min read",
    image: imgSpongeIron,
    relatedMaterial: "sponge-iron",
    relatedCity: "raipur",
    sections: [
      {
        id: "metallisation",
        heading: "Metallisation is the price",
        paragraphs: [
          "Sponge iron is bought for the metallic iron it delivers into the furnace. A four-point metallisation difference changes yield enough to swamp a ₹500 per tonne rate advantage. Always convert quotes to cost per tonne of liquid metal before comparing.",
        ],
      },
      {
        id: "fines",
        heading: "Lump-to-fines ratio",
        paragraphs: [
          "Fines behave differently in charging and carry a different price. Insist that the lump/fines split is declared per lot and verified at delivery, otherwise the mix silently shifts the economics against the buyer.",
        ],
      },
      {
        id: "logistics",
        heading: "Freight from the kiln belt",
        paragraphs: [
          "Most Indian DRI capacity sits in the Raipur–Bhilai and Rourkela belts. Buyers in the north and south should model road freight and transit time into landed cost, and track vehicles in real time so the charge plan does not stall waiting on a truck.",
        ],
      },
    ],
  },
  {
    slug: "green-steel-scrap-circularity-india",
    title: "Green Steel Starts in the Scrap Yard",
    excerpt:
      "Every tonne of scrap melted instead of ore avoids roughly 1.5 tonnes of CO2. Why organised scrap sourcing is India's cheapest decarbonisation lever.",
    category: "Sustainable Steel",
    author: "Jayanta Goswami",
    authorRole: "Co-Founder & Head of Operations",
    date: "June 26, 2026",
    isoDate: "2026-06-26",
    readTime: "7 min read",
    image: imgCircular,
    sections: [
      {
        id: "arithmetic",
        heading: "The arithmetic of secondary steel",
        paragraphs: [
          "Melting scrap in an induction furnace or EAF avoids the reduction step that dominates the carbon footprint of primary steelmaking. At national scale, raising the scrap share of the charge mix is the fastest emissions reduction available to Indian steel without new capital plant.",
        ],
      },
      {
        id: "bottleneck",
        heading: "The bottleneck is not technology, it is trust",
        paragraphs: [
          "India's collection system is fragmented across lakhs of informal yards. Furnaces would happily raise scrap share if quality and weight were predictable. The constraint is verification — grade evidence, honest weighment and payment certainty — not furnace capability.",
        ],
      },
      {
        id: "what-changes",
        heading: "What an organised marketplace changes",
        paragraphs: [
          "When each lot carries verified tonnage, grade evidence and escrow-backed settlement, buyers can substitute domestic scrap for imported material and for ore-based feed with confidence. That substitution is where the carbon saving actually happens.",
        ],
        bullets: [
          "Verified counterparties reduce rejection and re-handling",
          "Weight validation removes the incentive to over-declare",
          "Traceable invoices support customer-facing emissions reporting",
        ],
      },
    ],
  },
  {
    slug: "emerging-foundry-hubs-supply-strategy",
    title: "Supplying India's Emerging Foundry Hubs",
    excerpt:
      "Rajkot, Coimbatore, Hyderabad and Nagpur are growing faster than their local scrap generation. That gap is the single biggest opportunity for yards.",
    category: "Market Trends",
    author: "LohaLink Market Desk",
    authorRole: "Price benchmarking team",
    date: "June 18, 2026",
    isoDate: "2026-06-18",
    readTime: "5 min read",
    image: imgEmergingHubs,
    relatedMaterial: "cast-iron",
    relatedCity: "rajkot",
    sections: [
      {
        id: "gap",
        heading: "Where consumption outruns collection",
        paragraphs: [
          "Casting clusters in Rajkot and Coimbatore, TMT capacity around Hyderabad and re-rolling around Nagpur all consume more ferrous feed than their districts generate. They import tonnage from eastern and northern collection belts, and they pay a freight-inclusive premium for reliability.",
        ],
      },
      {
        id: "what-they-want",
        heading: "What these buyers actually pay for",
        paragraphs: [
          "Consistency beats price. A yard that delivers the declared grade at the declared weight, twice a month, becomes a preferred supplier and stops competing on rate alone.",
        ],
      },
      {
        id: "how-to-enter",
        heading: "How a yard enters a distant market",
        paragraphs: [
          "Start with one grade, one buyer and one documented dispatch. Escrow removes the payment risk that normally rules out long-distance first transactions, and tracked transit gives the buyer the visibility they need to commit to a repeat order.",
        ],
      },
    ],
  },
  {
    slug: "ai-vision-grading-disputes",
    title: "How AI Vision Grading Cuts Quality Disputes",
    excerpt:
      "Photographic grade analysis at listing time, weight validation at both ends, and a 48-hour resolution window — the anatomy of a dispute-free trade.",
    category: "Scrap Quality & Grading",
    author: "LohaLink Product Team",
    authorRole: "Platform engineering",
    date: "June 9, 2026",
    isoDate: "2026-06-09",
    readTime: "5 min read",
    image: imgAiQuality,
    relatedMaterial: "shredded-scrap",
    sections: [
      {
        id: "where-disputes-start",
        heading: "Disputes start before dispatch",
        paragraphs: [
          "Almost every quality dispute traces back to an ambiguous listing: a grade claimed without evidence, or a photograph that shows the best corner of the pile. Fixing the listing fixes the dispute.",
        ],
      },
      {
        id: "vision",
        heading: "What vision analysis can and cannot do",
        paragraphs: [
          "Image analysis reliably flags visible contamination, mixed thickness and non-ferrous attachments, and it benchmarks the asking price against the market band. It cannot certify chemistry — that still needs a lab or a mill test certificate, and buyers should insist on one for finished steel.",
        ],
      },
      {
        id: "resolution",
        heading: "When numbers still disagree",
        paragraphs: [
          "If seller and buyer weighbridge readings differ, both slips are uploaded and an inspector adjudicates within 48 hours. Escrow releases only the approved tonnage and refunds the balance, so neither party is exposed while the review runs.",
        ],
      },
    ],
  },
  {
    slug: "escrow-vs-credit-scrap-trade",
    title: "Escrow vs 30-Day Credit: Pricing Payment Risk Honestly",
    excerpt:
      "Credit is not free — it is a discount you pay in working capital and bad debt. A frank comparison of settlement structures in the Indian scrap trade.",
    category: "Policy & GST",
    author: "Kaustav Deb",
    authorRole: "Founder, LohaLink",
    date: "May 28, 2026",
    isoDate: "2026-05-28",
    readTime: "6 min read",
    image: imgEscrowCompliance,
    relatedMaterial: "ms-billets",
    sections: [
      {
        id: "true-cost",
        heading: "The true cost of credit terms",
        paragraphs: [
          "A yard extending 30-day credit finances the buyer's furnace. At realistic borrowing costs that is ₹350–₹600 per tonne, before accounting for the occasional total loss. Sellers who price credit properly usually discover that a lower headline rate with escrow settlement nets more.",
        ],
      },
      {
        id: "escrow",
        heading: "What escrow actually guarantees",
        paragraphs: [
          "Funds are locked at booking, so the seller knows the money exists before the truck loads. Release happens against verified delivery, so the buyer is never paying for weight that did not arrive. Neither side holds the other's money.",
        ],
        bullets: [
          "Seller sees confirmed funds before dispatch",
          "Buyer pays only for verified net weight",
          "Disputed tonnage is held, not litigated",
        ],
      },
      {
        id: "when-credit",
        heading: "When credit still makes sense",
        paragraphs: [
          "Long-standing relationships with audited counterparties and predictable monthly offtake can justify terms. For first transactions, distant counterparties and volatile grades, escrow is the cheaper structure once risk is priced in.",
        ],
      },
    ],
  },
];

export const getInsightBySlug = (slug: string) =>
  INSIGHTS.find((a) => a.slug === slug.toLowerCase());

export const getRelatedInsights = (slug: string, count = 3) => {
  const current = getInsightBySlug(slug);
  if (!current) return INSIGHTS.slice(0, count);
  const same = INSIGHTS.filter((a) => a.slug !== slug && a.category === current.category);
  const rest = INSIGHTS.filter((a) => a.slug !== slug && a.category !== current.category);
  return [...same, ...rest].slice(0, count);
};


export const insightCanonical = (slug: string) => `https://lohalink.in/insights/${slug}`;

export const buildInsightArticleLd = (article: InsightArticle) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: article.title,
  description: article.excerpt,
  articleSection: article.category,
  datePublished: article.isoDate,
  dateModified: article.isoDate,
  author: { "@type": "Person", name: article.author, jobTitle: article.authorRole },
  publisher: {
    "@type": "Organization",
    name: "LohaLink",
    url: "https://lohalink.in/",
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": insightCanonical(article.slug) },
  url: insightCanonical(article.slug),
  wordCount: article.sections.reduce(
    (n, s) => n + s.paragraphs.join(" ").split(/\s+/).length,
    0,
  ),
});

export const buildInsightBreadcrumbLd = (article: InsightArticle) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://lohalink.in/" },
    { "@type": "ListItem", position: 2, name: "Market Insights", item: "https://lohalink.in/insights" },
    { "@type": "ListItem", position: 3, name: article.title, item: insightCanonical(article.slug) },
  ],
});
